import { Box, Stack, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

export function DiffBody(props: {
    beforeText: string;
    afterText: string;
    beforeLabel: string;
    afterLabel: string;
}) {
    const theme = useTheme();

    return (
        <Box sx={{ overflowX: "auto" }}>
            <ReactDiffViewer
                oldValue={props.beforeText}
                newValue={props.afterText}
                leftTitle={`${props.beforeLabel} (before)`}
                rightTitle={`${props.afterLabel} (after)`}
                hideSummary
                splitView
                compareMethod={DiffMethod.WORDS}
                useDarkTheme={false}
                hideLineNumbers={false}
                showDiffOnly
                extraLinesSurroundingDiff={3}
                styles={{
                    variables: {
                        light: {
                            diffViewerTitleColor: theme.palette.gray[500],
                        },
                    },
                    titleBlock: {
                        // The library fixes this row at 2.4em with overflow hidden, an exact fit that clips the title.
                        lineHeight: 1,
                        height: "auto",
                        padding: "0rem 1rem",
                        fontWeight: 700,
                    },
                    diffContainer: {
                        // The library defaults to min width 1000px, which can cause the page to horizontally scroll.
                        minWidth: 0,
                    },
                }}
            />
        </Box>
    );
}

export function EmptyDiffCard(props: {
    icon: ReactNode;
    title: string;
    message: string;
}) {
    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: 2,
                backgroundColor: "white.main",
                px: 2,
                py: 1.5,
            })}
        >
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
                {props.icon}
                <Typography fontSize="15px" fontWeight={600}>
                    {props.title}
                </Typography>
            </Stack>
            <Typography color="gray.500" variant="body2">
                {props.message}
            </Typography>
        </Box>
    );
}

export function CountChip(props: { count: number; tone: "added" | "removed" }) {
    if (props.count === 0) return null;

    const added = props.tone === "added";
    return (
        <Typography
            variant="body2"
            fontWeight={600}
            px={1}
            borderRadius={1}
            sx={(theme) => ({
                color: added
                    ? theme.palette.green.main
                    : theme.palette.error[700],
                backgroundColor: added
                    ? theme.palette.green.background
                    : theme.palette.error[100],
            })}
        >
            {added ? `+${props.count} added` : `−${props.count} removed`}
        </Typography>
    );
}
